/**
 * NOPA Racquet Serial Number Verification
 *
 * Decodes a 7-digit NOPA serial number: [Year][Series][Model][Production 0001-9999]
 * This is a pattern/structure check against the published numbering scheme —
 * it does not confirm the number was issued against a real production run.
 *
 * Series map is fixed by the manual (do not change without updating docs):
 *   1 Young Heritage, 2 Classic, 3 Heritage, 4 Vintage, 5 Limited Edition
 * Model names AND photos within each series are theme-editor-configurable (see section blocks, one block per model).
 */
(() => {
  const DEPARTMENT = 'Racquets';
  // Year digit -> century assumption. NOPA serials started in the 2020s, so
  // "6" => 2026. Bump the base decade here if the brand crosses into the 2030s.
  const YEAR_BASE = 2020;

  class NopaSnVerify {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-sn-form]');
      this.input = root.querySelector('[data-sn-input]');
      this.errorEl = root.querySelector('[data-sn-error]');
      this.result = root.querySelector('[data-sn-result]');
      this.resultImage = root.querySelector('[data-sn-result-image]');
      this.resultTitle = root.querySelector('[data-sn-result-title]');
      this.resetBtn = root.querySelector('[data-sn-reset]');
      this.newsletterTags = root.querySelector('[data-sn-newsletter-tags]');

      const sectionId = root.getAttribute('data-section-id');
      this.imagesWrap = document.getElementById(`SnImages-${sectionId}`);

      const dataEl = document.getElementById(`SnSeriesData-${sectionId}`);
      this.data = { seriesNames: {}, models: {} };
      if (dataEl) {
        try {
          this.data = JSON.parse(dataEl.textContent.trim());
        } catch (err) {
          this.data = { seriesNames: {}, models: {} };
        }
      }

      this.bind();
    }

    bind() {
      if (!this.form || !this.input) return;

      this.input.addEventListener('input', () => {
        this.input.value = this.input.value.replace(/\D/g, '').slice(0, 7);
        this.clearError();
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.verify();
      });

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => this.reset());
      }
    }

    clearError() {
      if (this.errorEl) this.errorEl.textContent = '';
    }

    showError(message) {
      if (this.errorEl) this.errorEl.textContent = message;
    }

    verify() {
      const digits = this.input.value.replace(/\D/g, '');

      if (digits.length !== 7) {
        this.showError('Enter the full 7-digit serial number.');
        return;
      }

      const yearDigit = digits[0];
      const seriesDigit = digits[1];
      const modelDigit = digits[2];
      const production = digits.slice(3);

      const seriesName = this.data.seriesNames && this.data.seriesNames[seriesDigit];
      if (!seriesName) {
        this.showError("We couldn't recognize that serial number. Double-check the digits and try again.");
        return;
      }

      const seriesModels = this.data.models && this.data.models[seriesDigit];
      const modelName = seriesModels && seriesModels[modelDigit];
      if (!modelName) {
        this.showError("We couldn't recognize that serial number. Double-check the digits and try again.");
        return;
      }

      const yearFull = YEAR_BASE + parseInt(yearDigit, 10);

      this.showResult({
        id: digits,
        department: DEPARTMENT,
        type: seriesName,
        product: modelName,
        year: String(yearFull),
        production,
        seriesDigit,
        modelDigit,
      });
    }

    showResult(data) {
      this.clearError();

      if (this.resultTitle) {
        this.resultTitle.textContent = `${data.type}, ${data.product}`;
      }

      this.root.querySelectorAll('[data-sn-field]').forEach((el) => {
        const key = el.getAttribute('data-sn-field');
        if (key && data[key] !== undefined) {
          el.textContent = data[key];
        }
      });

      this.setResultImage(`${data.seriesDigit}-${data.modelDigit}`);

      if (this.newsletterTags) {
        const slug = (value) => String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const tags = [
          'newsletter',
          'sn-lookup',
          `sn-series-${slug(data.type)}`,
          `sn-model-${slug(data.product)}`,
          `sn-${data.id}`,
        ];
        this.newsletterTags.value = tags.join(', ');
      }

      if (this.form) this.form.hidden = true;
      if (this.result) {
        this.result.hidden = false;
        // Force reflow so the transition runs.
        // eslint-disable-next-line no-unused-expressions
        this.result.offsetHeight;
        this.result.classList.add('is-visible');
        this.result.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      this.dispatchAnalytics(data);
    }

    setResultImage(imageKey) {
      if (!this.resultImage || !this.imagesWrap) return;

      const source = this.imagesWrap.querySelector(`[data-sn-image-source="${imageKey}"]`);
      const existing = this.resultImage.querySelector('img, svg, .placeholder-svg');
      if (existing) existing.remove();

      if (source) {
        const clone = source.firstElementChild ? source.firstElementChild.cloneNode(true) : null;
        if (clone) this.resultImage.prepend(clone);
      }
    }

    dispatchAnalytics(data) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'nopa_serial_verified',
          sn_id: data.id,
          sn_series: data.type,
          sn_model: data.product,
        });
      } catch (err) {
        // dataLayer not available — no-op.
      }

      this.root.dispatchEvent(
        new CustomEvent('nopa:sn-verified', {
          bubbles: true,
          detail: data,
        })
      );
    }

    reset() {
      this.clearError();
      if (this.result) {
        this.result.classList.remove('is-visible');
        this.result.hidden = true;
      }
      if (this.form) this.form.hidden = false;
      if (this.input) {
        this.input.value = '';
        this.input.focus();
      }
    }
  }

  document.querySelectorAll('[data-sn-verify-root]').forEach((root) => {
    new NopaSnVerify(root);
  });
})();
